// NTP-style clock synchronisation between a browser and the Durable Object.
//
// Every ping records when it left (t0) and when the pong came back (t1) on the
// client clock, plus the server timestamp (s) stamped in between. Assuming the
// path is symmetric, the server clock read s at local time (t0 + t1) / 2. The
// samples with the lowest round-trip time are the least disturbed by queueing,
// so we estimate the offset from the best few of them.

export interface ClockSample {
  rtt: number;
  offset: number;
  at: number;
}

export class ClockSync {
  private samples: ClockSample[] = [];
  private readonly maxSamples: number;
  private cachedOffset = 0;
  private cachedRtt = Infinity;

  constructor(maxSamples = 24) {
    this.maxSamples = maxSamples;
  }

  add(t0: number, serverMs: number, t1: number): ClockSample {
    const rtt = Math.max(0, t1 - t0);
    const offset = serverMs - (t0 + t1) / 2;
    const s: ClockSample = { rtt, offset, at: t1 };
    this.samples.push(s);
    if (this.samples.length > this.maxSamples) this.samples.shift();
    this.recompute();
    return s;
  }

  private recompute(): void {
    const sorted = [...this.samples].sort((a, b) => a.rtt - b.rtt);
    const best = sorted.slice(0, Math.max(1, Math.ceil(sorted.length / 3)));
    const offsets = best.map((s) => s.offset).sort((a, b) => a - b);
    const mid = offsets.length >> 1;
    this.cachedOffset =
      offsets.length % 2 ? offsets[mid]! : (offsets[mid - 1]! + offsets[mid]!) / 2;
    this.cachedRtt = sorted[0]!.rtt;
  }

  get ready(): boolean {
    return this.samples.length > 0;
  }

  get sampleCount(): number {
    return this.samples.length;
  }

  /** Estimated server clock minus local clock, in ms. */
  get offset(): number {
    return this.cachedOffset;
  }

  /** Best observed round-trip time, in ms. */
  get rtt(): number {
    return this.cachedRtt;
  }

  /** Median RTT of recent samples — a better "how does it feel" number than the minimum. */
  get typicalRtt(): number {
    if (!this.samples.length) return Infinity;
    const r = this.samples.slice(-8).map((s) => s.rtt).sort((a, b) => a - b);
    return r[r.length >> 1]!;
  }

  serverNow(localNow: number): number {
    return localNow + this.cachedOffset;
  }

  reset(): void {
    this.samples = [];
    this.cachedOffset = 0;
    this.cachedRtt = Infinity;
  }
}
